import React, { useState, useEffect } from 'react';
import {
  Grid,
  Button,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { AutocompleteV, ButtonLoading, InputV2, Icons } from 'components';
import { useForm, Controller } from 'react-hook-form';
import { useStyles } from './style';
import createToast from 'components/Toast/Toast';
import { useBusiness } from 'hooks';
import DialogSettlement from './components/DialogSettlement';
import { getSheba, autoSettlement, getAutoSettlement } from './services/AutoSettlementServices';
import { CheckBoxIcon, CheckBoxIconFill } from 'components/CheckBox/CheckBoxIcon';

const AutoSettlement = ({ guildItem, onEnd = () => { }, onRefreshList, ...props }) => {
  const [list, setList] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [autoSettle, setAutoSettle] = useState();
  const [selectedValue, setSelectedValue] = useState('');
  const [shebasLoading, setShebasLoading] = useState(false);
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);
  const [autoSettleLoading, setAutoSettleLoading] = useState(false);
  const [currentAutoSettleInfo, setCurrentAutoSettleInfo] = useState(null);
  //* * HOOKS */
  const Business = useBusiness();
  const { handleSubmit, control, formState: { errors }, register } = useForm({});
  const classes = useStyles();

  useEffect(() => {
    if (guildItem?.id) {
      setAutoSettle(!!guildItem?.isAutoSettle);
    }
    getSettlementInfo();
  }, [guildItem, Business]);

  useEffect(() => {
    if (!guildItem?.guildSrv?.code || !guildItem?.isAutoSettle) {
      getShebas();
    }
  }, []);

  //* * API REQUESTS */
  const getSettlementInfo = async () => {
    if (!guildItem?.guildSrv?.code || !guildItem?.isAutoSettle) return;
    try {
      setAutoSettleLoading(true);
      const params = {
        guildCode: guildItem.guildSrv.code,
      };
      const result = await getAutoSettlement(params);
      const data = result.result;
      if (data) {
        setCurrentAutoSettleInfo(data);
      }
    } catch (error) { }
    setAutoSettleLoading(false);
  };

  const getShebas = async () => {
    try {
      setShebasLoading(true);
      const params = { offset: 0, size: 50 };
      const result = await getSheba(params);
      const data = result.result ?? [];
      const sheba = data?.map((v) => ({
        label: v.shebaNumber,
        value: v.shebaNumber,
      }));
      setList(sheba);
    } catch (error) { }
    setShebasLoading(false);
  };

  const onSubmit = async (data) => {
    const shebaNo = data?.sheba?.value ?? data?.sheba ?? null;
    if (!shebaNo) return createToast('خطا', 'شماره شبا را وارد یا انتخاب کنید', 'danger');

    setIsLoadingBtn(true);
    try {
      const param = {
        guildCode: `${guildItem.guildSrv.code}`,
        sheba: `${shebaNo}`,
        paymentId: data?.paymentId?.length > 0 ? data?.paymentId : null,
        instant: data.instant === undefined ? false : data.instant,
      };
      await autoSettlement(param);
      setIsLoadingBtn(false);
      setAutoSettle(true);
      createToast('موفق', 'تسویه خودکار با موفقیت فعال شد!', 'success');

      onRefreshList();
      onEnd();
    } catch (error) {
      setIsLoadingBtn(false);
    }
  };

  //* * OTHER FUNCTIONS */
  const handleDelete = async () => {
    setOpenDialog(true);
    setSelectedValue(guildItem.guildSrv.code);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <>
      {autoSettleLoading ? (
        <Grid container justifyContent="center" alignItems="center">
          <CircularProgress size={32} style={{ marginTop: 16, marginBottom: 16 }} />
        </Grid>
      ) : autoSettle ? (
        <>
          <Typography>
            شما برای این حساب تفصیلی، تسویه خودکار را با اطلاعات زیر فعال کرده‌اید:
          </Typography>
          <Typography className={classes.sheba}>
            <b>
              شماره شبای مقصد:{' '}
              {currentAutoSettleInfo?.toolId ? `IR${currentAutoSettleInfo?.toolId}` : '-'}
            </b>
          </Typography>
          {currentAutoSettleInfo?.paymentId ? (
            <Typography className={classes.sheba}>
              شناسه واریز: <b>{currentAutoSettleInfo?.paymentId}</b>
            </Typography>
          ) : (
            ''
          )}
          <div className={classes.titleAct}>جهت غیرفعال نمودن، از دکمه زیر استفاده نمایید:</div>
          <Button
            variant="contained"
            disabled={isLoadingBtn}
            onClick={handleDelete}
            color="primary"
          >
            غیر فعال کردن برداشت خودکار
            {isLoadingBtn && <CircularProgress size={24} />}
          </Button>
        </>
      ) : (
        <Grid item xs={12}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container direction="row" alignItems="center" spacing={0}>
              <Grid item xs={12}>

                <Grid
                  className={classes.dangerBox}
                  container
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                >
                  <Grid item>
                    <Icons icon="Info" color="#209CD7" size="28" />
                  </Grid>
                  <Grid item xs>
                    <Typography className={classes.dangerText}>

                      برای ثبت تسویه خودکار نیاز به تایید شماره شبا توسط تیم پشتیبانی می باشد. جهت
                      تایید، شماره شبا باید به اسم مالک کسب وکار (حقیقی ها) و به اسم شرکت مربوطه یا یکی
                      از صاحبان امضا(حقوقی ها) باشد.
                    </Typography>
                  </Grid>
                </Grid>

              </Grid>

              <Grid item xs={12}>
                <AutocompleteV
                  control={control}
                  errors={errors}
                  register={register}
                  placeholder="شماره شبای عددی بدون IR ابتدایی"
                  name="sheba"
                  label="شماره شبای مورد نظر برای تسویه خودکار"
                  rules={['required']}
                  list={list}
                  loading={shebasLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <InputV2
                  fullWidth
                  label="شناسه واریز"
                  errors={errors}
                  name="paymentId"
                  register={register}
                  control={control}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="instant"
                  control={control}
                  render={({ field: { onChange, value, ...rest } }) => {
                    return (
                      <FormControlLabel
                        value={value}
                        className={classes.oneTimeCheck}
                        control={
                          <Checkbox
                            style={{ color: '#448aff' }}
                            onChange={(e) => onChange(e.target.checked)}
                            checked={value}
                            icon={<CheckBoxIcon />}
                            checkedIcon={<CheckBoxIconFill />}
                          />
                        }
                        label="تسویه خودکار فوری"
                      />
                    );
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                style={{ display: 'flex', marginTop: '16px', flexDirection: 'row-reverse', gap: '15px' }}
              >
                <ButtonLoading
                  name="ثبت تسویه خودکار"
                  loading={isLoadingBtn}
                  variant="contained"
                  color="primary"
                  type="submit"
                />
                <Button
                  variant="contained"
                  onClick={() => onEnd() /* navigate('/Guilds') */}
                  style={{ marginRight: 10 }}
                >
                  انصراف
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      )}

      <DialogSettlement
        selectedValue={selectedValue}
        setAutoSettle={setAutoSettle}
        open={openDialog}
        onClose={handleCloseDialog}
        onEnd={() => {
          onEnd();
          onRefreshList();
        }}
      />
    </>
  );
};

export default AutoSettlement;
